import { isLastOfMonth, isDateValid } from "./DateUtils";
import moment from "moment"; // #region CONSTANTES

var DEFAULT_FREQUENCY = "11111001111111111111111110";
var DEFAULT_FREQUENCY_PEGASE = "11111001111111111111111110.33"; // #endregion
// #region CLASS

/**
 * Une classe de gestion de fréquence journalière
 * @category Utils
 * @hideconstructor
 */

var FrequencyUtils = /*#__PURE__*/function () {
  function FrequencyUtils() {}

  /**
   * Inverse les jours sélectionnés
   * @param {string} frequencyStr Une fréquence sous forme de chaîne de 0 et de 1
   * @returns {string} Une chaîne de caractères
   */
  FrequencyUtils.flipDays = function flipDays(frequencyStr) {
    if (!frequencyStr) {
      return "";
    }

    var obj = FrequencyUtils.toObject(frequencyStr);
    obj.LUNDI = !obj.LUNDI;
    obj.MARDI = !obj.MARDI;
    obj.MERCREDI = !obj.MERCREDI;
    obj.JEUDI = !obj.JEUDI;
    obj.VENDREDI = !obj.VENDREDI;
    obj.SAMEDI = !obj.SAMEDI;
    obj.DIMANCHE = !obj.DIMANCHE;
    return FrequencyUtils.toString(obj);
  };

  /**
   * Inverse les jours du mois sélectionnés
   * @param {string} frequencyStr Une fréquence sous forme de chaîne de 0 et de 1
   * @returns {string} Une chaîne de caractères
   */
  FrequencyUtils.flipDaysOfMonth = function flipDaysOfMonth(frequencyStr) {
    if (!frequencyStr) {
      return "";
    }

    var obj = FrequencyUtils.toObject(frequencyStr);
    obj.SEM1 = !obj.SEM1;
    obj.SEM2 = !obj.SEM2;
    obj.SEM3 = !obj.SEM3;
    obj.SEM4 = !obj.SEM4;
    obj.SEM5 = !obj.SEM5;
    obj.SEMD = !obj.SEMD;
    return FrequencyUtils.toString(obj);
  };

  /**
   * Inverse les mois sélectionnés
   * @param {string} frequencyStr Une fréquence sous forme de chaîne de 0 et de 1
   * @returns {string} Une chaîne de caractères
   */
  FrequencyUtils.flipMonths = function flipMonths(frequencyStr) {
    if (!frequencyStr) {
      return "";
    }

    var obj = FrequencyUtils.toObject(frequencyStr);
    obj.JANVIER = !obj.JANVIER;
    obj.FEVRIER = !obj.FEVRIER;
    obj.MARS = !obj.MARS;
    obj.AVRIL = !obj.AVRIL;
    obj.MAI = !obj.MAI;
    obj.JUIN = !obj.JUIN;
    obj.JUILLET = !obj.JUILLET;
    obj.AOUT = !obj.AOUT;
    obj.SEPTEMBRE = !obj.SEPTEMBRE;
    obj.OCTOBRE = !obj.OCTOBRE;
    obj.NOVEMBRE = !obj.NOVEMBRE;
    obj.DECEMBRE = !obj.DECEMBRE;
    return FrequencyUtils.toString(obj);
  };

  /**
   * Renvoie la fréquence par défaut
   * @param {boolean} pegaseFreq Indique si c'est une fréquence PEGASE-Propreté
   * @returns {string} La fréquence ar défaut
   */
  FrequencyUtils.getDefaultFrequency = function getDefaultFrequency(pegaseFreq) {
    if (pegaseFreq) {
      return DEFAULT_FREQUENCY_PEGASE;
    }

    return DEFAULT_FREQUENCY;
  }
  /**
   * Vérifie si la date est concernée par la fréquence
   * @param {date} date La date à tester
   * @param {string} freq La fréquence sous la forme d'une chaîne de 0 et de 1
   * @param {date} frestartDateq La date de début de la fréquence
   * @returns {boolean} true si la date est concernée par la fréquence
   */
  ;

  FrequencyUtils.isDateInFrequency = function isDateInFrequency(date, freq, startDate) {
    if (!freq) {
      return false;
    }

    var result = false; // On découpe les différentes parties de la fréquence

    var freqDays = freq.slice(0, 7);
    var freqDaysOfMonth = freq.slice(7, 13);
    var freqMonths = freq.slice(13, 25);
    var freqPeriodicity = freq.slice(25); // On récupère les informations de la date

    var day = date.getDay() > 0 ? date.getDay() - 1 : 6; // L = 0, Ma = 1, Me = 2, J = 3, V = 4, S = 5, D = 6

    var dayOfMonth = Math.floor((date.getDate() - 1) / 7);
    var month = date.getMonth(); // Vérification du jour

    result = freqDays[day] === "1";

    if (result) {
      if (startDate) {
        // S'il y a une date de début de période, on vérifie que le jour correspond à la période
        result = moment(date).isAfter(moment(startDate)) && moment(date).diff(startDate, "weeks") % freqPeriodicity === 0;
      } else {
        // Vérification du jour du mois (y compris s'il s'agit du dernier jour du mois)
        result = freqDaysOfMonth[dayOfMonth] === "1" || freqDaysOfMonth[5] === "1" && isLastOfMonth(date);
      }
    } // Vérification du mois


    if (result) {
      result = freqMonths[month] === "1";
    }

    return result;
  };

  /**
   * Vérifie si la fréquence est valide
   * @param {string} frequencyStr La fréquence sous la forme d'une chaîne de 0 et de 1
   * @param {date} startDate La date de début dans le cas de "Toutes les X semaines"
   * @param {boolean} periodicity Indique si l'on est en mode "Toutes les X semaines"
   * @returns {boolean} true si la fréquence est valide
   */
  FrequencyUtils.isFrequencyValid = function isFrequencyValid(frequencyStr, startDate, periodicity) {
    if (!frequencyStr) {
      return false;
    } // On découpe la chaîne passée en paramètres en jours / jours du mois / mois


    var days = frequencyStr.slice(0, 7);
    var daysOfMonth = frequencyStr.slice(7, 13);
    var months = frequencyStr.slice(13, 25);
    var freqPeriodicity = Number(frequencyStr.slice(25)); // On compte le nombre d'occurences dans chaque partie

    var nbDays = (days.match(/1/g) || []).length;
    var nbDaysOfMonth = (daysOfMonth.match(/1/g) || []).length;
    var nbMonths = (months.match(/1/g) || []).length;

    if (periodicity) {
      // En mode "Toutes les X semaines", il faut au moins un jour, un mois, X > 1 et une date de début
      return nbDays > 0 && nbMonths > 0 && freqPeriodicity > 1 && startDate !== null && isDateValid(startDate);
    } // On retourne true s'il y a au moins une occurrence sur chaque partie


    return nbDays > 0 && nbDaysOfMonth > 0 && nbMonths > 0;
  };

  /**
   * Vérifie si la fréquence est périodique
   * @param {string} frequencyStr La fréquence sous la forme d'une chaîne de 0 et de 1
   * @param {date} startDate La date de début dans le cas de "Toutes les X semaines"
   * @param {boolean} pegaseFreq Indique si c'est une fréquence PEGASE-Propreté
   * @returns {boolean} true si la fréquence est périodique
   */
  FrequencyUtils.isFrequencyPeriodicity = function isFrequencyPeriodicity(frequencyStr, startDate, pegaseFreq) {
    if (!frequencyStr) {
      return false;
    }

    return startDate !== null && isDateValid(startDate) || FrequencyUtils.toObject(frequencyStr, pegaseFreq).SEMX > 1;
  };

  /**
   * Renvoie la fréquence sous forme d'objet
   * @param {string} frequencyStr La fréquence sous forme d'une chaîne de 0 et de 1
   * @param {boolean} pegaseFreq Indique si c'est une fréquence PEGASE-Propreté
   * @returns {object} Un objet représentant la fréquence
   */
  FrequencyUtils.toObject = function toObject(frequencyStr, pegaseFreq) {
    if (!frequencyStr) {
      return {};
    } // On vérifie que la chaîne fournie à la bonne longueur


    if (frequencyStr.length < 26) {
      throw "La fréquence fournie n'a pas la bonne longueur (au moins 26 caractères)";
    }

    var obj = {}; // On assigne chaque jour / jour du mois / mois

    obj.LUNDI = frequencyStr[0] === "1";
    obj.MARDI = frequencyStr[1] === "1";
    obj.MERCREDI = frequencyStr[2] === "1";
    obj.JEUDI = frequencyStr[3] === "1";
    obj.VENDREDI = frequencyStr[4] === "1";
    obj.SAMEDI = frequencyStr[5] === "1";
    obj.DIMANCHE = frequencyStr[6] === "1";
    obj.SEM1 = frequencyStr[7] === "1";
    obj.SEM2 = frequencyStr[8] === "1";
    obj.SEM3 = frequencyStr[9] === "1";
    obj.SEM4 = frequencyStr[10] === "1";
    obj.SEM5 = frequencyStr[11] === "1";
    obj.SEMD = frequencyStr[12] === "1";
    obj.JANVIER = frequencyStr[13] === "1";
    obj.FEVRIER = frequencyStr[14] === "1";
    obj.MARS = frequencyStr[15] === "1";
    obj.AVRIL = frequencyStr[16] === "1";
    obj.MAI = frequencyStr[17] === "1";
    obj.JUIN = frequencyStr[18] === "1";
    obj.JUILLET = frequencyStr[19] === "1";
    obj.AOUT = frequencyStr[20] === "1";
    obj.SEPTEMBRE = frequencyStr[21] === "1";
    obj.OCTOBRE = frequencyStr[22] === "1";
    obj.NOVEMBRE = frequencyStr[23] === "1";
    obj.DECEMBRE = frequencyStr[24] === "1";
    obj.SEMX = frequencyStr.slice(25); // SEMX contient le nombre de semaines d'interval

    if (pegaseFreq && obj.SEMX === ".33") {
      obj.SEMX = "0";
    }

    return obj;
  };

  /**
   * Renvoie la fréquence sous forme d'une phrase intelligible
   * @param {string} frequencyStr Une fréquence sous forme de chaîne de 0 et de 1
   * @returns {string} Une chaîne de caractères
   */
  FrequencyUtils.toSentence = function toSentence(frequencyStr) {
    if (!frequencyStr) {
      return "Pas de fréquence de défini";
    }

    var DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    var DAYSOFMONTH = ["premier", "deuxième", "troisième", "quatrième", "cinquième", "dernier"];
    var MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]; // Transforme une fréquence en une phrase intelligible

    function arrange(str, vals) {
      var result = ""; // On créé un tableau contenant les valeurs retenues

      var tab1 = [];

      for (var i = 0; i < str.length; i++) {
        if (str[i] === "1") {
          tab1.push(vals[i]);
        }
      } // On transforme le tableau en une chaîne intelligible pour un humain


      if (tab1.length === 1) {
        result = tab1[0];
      } else if (tab1.length > 1) {
        var first = tab1.slice(0, tab1.length - 1);
        var last = tab1[tab1.length - 1];
        result = first.join(", ") + " et " + last;
      }

      return result;
    }

    var result = ""; // On découpe la chaîne passée en paramètres en jours / jours du mois / mois

    var days = frequencyStr.slice(0, 7);
    var daysOfMonth = frequencyStr.slice(7, 13);
    var months = frequencyStr.slice(13, 25);
    var xWeek = frequencyStr.slice(25); // On compte le nombre d'occurences dans chaque partie

    var nbDays = (days.match(/1/g) || []).length;
    var nbDaysOfMonth = (daysOfMonth.match(/1/g) || []).length;
    var nbMonths = (months.match(/1/g) || []).length;
    var nbXWeek = parseInt(xWeek); // On vérifie qu'on a bien au moins une occurrence sur chaque partie, sinon on renvoie un message d'erreur

    var periodicity = !isNaN(nbXWeek) && nbXWeek > 1;

    if (nbDays > 0 && (nbDaysOfMonth > 0 || periodicity) && nbMonths > 0) {
      var deterDays;

      if (nbDays === 0) {
        return "";
      } else if (nbDays === 1) {
        deterDays = "Le";
      } else {
        deterDays = "Les";
      } // On transforme la fréquence en mots


      var strDays = arrange(days, DAYS);
      var strDaysOfMonth = "";
      var strXWeek = "";

      if (periodicity) {
        strXWeek = "toutes les " + nbXWeek + " semaines";
      } else {
        strDaysOfMonth = arrange(daysOfMonth, DAYSOFMONTH);
      }

      var strMonth = arrange(months, MONTHS); // On ajuste certains mots pour rendre la phrase plus cohérente

      if (nbDaysOfMonth === 6) {
        deterDays = "Tous";
        strDaysOfMonth = "les";

        if (nbDays === 7) {
          strDays = "jours";
        }
      }

      if (nbMonths === 12) {
        if (nbDaysOfMonth === 6) {
          strMonth = "l'année";
        } else {
          strMonth = "chaque mois";
        }
      } // On assemble la phrase


      result = deterDays + " " + (strDaysOfMonth ? strDaysOfMonth + " " : "") + strDays + " " + (strXWeek ? strXWeek + " " : "") + "de " + strMonth;
    } else {
      result = "Il faut sélectionner au moins un jour, un jour du mois et un mois";
    }

    return result;
  };

  /**
   * Renvoie la fréquence comme une chaîne de 0 et de 1
   * @param {object} frequencyObj Un objet fréquence
   * @param {boolean} pegaseFreq Indique si c'est une fréquence PEGASE-Propreté
   * @returns {string} Une chaîne de caractères
   */
  FrequencyUtils.toString = function toString(frequencyObj, pegaseFreq) {
    if (!frequencyObj) {
      return "";
    }

    var result = "";
    result = result.concat(frequencyObj.LUNDI ? "1" : "0");
    result = result.concat(frequencyObj.MARDI ? "1" : "0");
    result = result.concat(frequencyObj.MERCREDI ? "1" : "0");
    result = result.concat(frequencyObj.JEUDI ? "1" : "0");
    result = result.concat(frequencyObj.VENDREDI ? "1" : "0");
    result = result.concat(frequencyObj.SAMEDI ? "1" : "0");
    result = result.concat(frequencyObj.DIMANCHE ? "1" : "0");
    result = result.concat(frequencyObj.SEM1 ? "1" : "0");
    result = result.concat(frequencyObj.SEM2 ? "1" : "0");
    result = result.concat(frequencyObj.SEM3 ? "1" : "0");
    result = result.concat(frequencyObj.SEM4 ? "1" : "0");
    result = result.concat(frequencyObj.SEM5 ? "1" : "0");
    result = result.concat(frequencyObj.SEMD ? "1" : "0");
    result = result.concat(frequencyObj.JANVIER ? "1" : "0");
    result = result.concat(frequencyObj.FEVRIER ? "1" : "0");
    result = result.concat(frequencyObj.MARS ? "1" : "0");
    result = result.concat(frequencyObj.AVRIL ? "1" : "0");
    result = result.concat(frequencyObj.MAI ? "1" : "0");
    result = result.concat(frequencyObj.JUIN ? "1" : "0");
    result = result.concat(frequencyObj.JUILLET ? "1" : "0");
    result = result.concat(frequencyObj.AOUT ? "1" : "0");
    result = result.concat(frequencyObj.SEPTEMBRE ? "1" : "0");
    result = result.concat(frequencyObj.OCTOBRE ? "1" : "0");
    result = result.concat(frequencyObj.NOVEMBRE ? "1" : "0");
    result = result.concat(frequencyObj.DECEMBRE ? "1" : "0");
    result = result.concat(frequencyObj.SEMX > 1 ? frequencyObj.SEMX : pegaseFreq ? ".33" : "0");
    return result;
  };

  return FrequencyUtils;
}();

; // #endregion

export var freqFlipDays = FrequencyUtils.flipDays;
export var freqFlipDaysOfMonth = FrequencyUtils.flipDaysOfMonth;
export var freqFlipMonths = FrequencyUtils.flipMonths;
export var getDefaultFrequency = FrequencyUtils.getDefaultFrequency;
export var isDateInFrequency = FrequencyUtils.isDateInFrequency;
export var isFrequencyValid = FrequencyUtils.isFrequencyValid;
export var isFrequencyPeriodicity = FrequencyUtils.isFrequencyPeriodicity;
export var freqToObject = FrequencyUtils.toObject;
export var freqToString = FrequencyUtils.toString;
export var freqToSentence = FrequencyUtils.toSentence;